import { useEffect, useMemo, useState } from "react";
import { LogIn, KeyRound, ShieldAlert, Ban, ShieldX, ShieldCheck, Search, ChevronDown, MapPin, Smartphone, Monitor, Tablet, Wifi, Info, ExternalLink } from "lucide-react";
import api from "@/lib/api";
import { useAutoRefresh } from "@/hooks/useAutoRefresh";
import { Input } from "@/components/ui/input";
import { Segmented, StatCard, Empty } from "@/components/Shared";
import { BanList } from "@/components/security/BanList";
import { useSecurityOverview } from "@/components/security/SecurityPanel";
import { cn } from "@/lib/utils";

const KIND = {
  login_ok: { label: "Giriş yapıldı", icon: LogIn, tone: "bg-emerald-600", soft: "bg-emerald-500/10 text-emerald-700" },
  gate_ok: { label: "Giriş parolası geçildi", icon: KeyRound, tone: "bg-sky-600", soft: "bg-sky-500/10 text-sky-700" },
  gate_fail: { label: "Hatalı giriş parolası", icon: ShieldAlert, tone: "bg-amber-500", soft: "bg-amber-500/10 text-amber-700" },
  login_fail: { label: "Hatalı şifre / PIN", icon: ShieldAlert, tone: "bg-amber-500", soft: "bg-amber-500/10 text-amber-700" },
  ban: { label: "ERİŞİM ENGELLENDİ", icon: Ban, tone: "bg-red-600", soft: "bg-red-500/10 text-red-700" },
  blocked: { label: "Engelli bağlantı denemesi", icon: ShieldX, tone: "bg-red-600", soft: "bg-red-500/10 text-red-700" },
  unban: { label: "Engel kaldırıldı", icon: ShieldCheck, tone: "bg-violet-600", soft: "bg-violet-500/10 text-violet-700" },
};
const METHOD = { parola: "Giriş parolası", pin: "PIN", sifre: "Şifre", eposta: "E-posta + şifre" };
const CONN = { wifi: "Wi-Fi", cellular: "Mobil veri", ethernet: "Kablolu (Ethernet)", none: "Bağlantı yok", bluetooth: "Bluetooth" };
const KIND_ICON = { telefon: Smartphone, tablet: Tablet, bilgisayar: Monitor };
const fullTime = (iso) => { try { return new Date(iso).toLocaleString("tr-TR", { weekday: "long", day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" }); } catch { return iso; } };
const clock = (iso) => { try { return new Date(iso).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit", second: "2-digit" }); } catch { return ""; } };
const dayOf = (iso) => { try { return new Date(iso).toLocaleDateString("tr-TR", { day: "2-digit", month: "long", year: "numeric", weekday: "long" }); } catch { return ""; } };

const place = (g) => (g?.private ? "Yerel / iç ağ" : [g?.city, g?.region !== g?.city ? g?.region : "", g?.country].filter(Boolean).join(", "));
const connOf = (e) => CONN[e.client?.conn_type] || (e.geo?.mobile_net ? "Mobil şebeke (operatör IP'si)" : "");

function rows(e) {
  const g = e.geo || {}, c = e.client || {}, d = e.device || {};
  const map = g.lat && g.lon ? <a href={`https://www.openstreetmap.org/?mlat=${g.lat}&mlon=${g.lon}#map=12/${g.lat}/${g.lon}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline">{Number(g.lat).toFixed(4)}, {Number(g.lon).toFixed(4)}<ExternalLink className="h-3 w-3" /></a> : "";
  return [
    ["Tarih / saat", fullTime(e.at)],
    ["Cihazın kendi saati", c.local_time],
    ["Seçilen kullanıcı", e.user_name ? `${e.user_name}${e.user_role ? ` (${e.user_role === "admin" ? "Yönetici" : "Personel"})` : ""}` : ""],
    ["Yazılan e-posta", e.email],
    ["Giriş yöntemi", METHOD[e.method] || e.method],
    ["Sonuç", e.detail],
    ["IP adresi", e.ip ? `${e.ip}${e.trusted_ip ? " · güvenilir ağ" : ""}` : ""],
    ["IP zinciri (vekil)", e.ip_chain],
    ["Konum (IP'ye göre, tahmini)", [g.flag, place(g), g.postal ? `PK ${g.postal}` : "", g.continent].filter(Boolean).join(" · ")],
    ["Koordinat", map],
    ["IP saat dilimi", g.timezone ? `${g.timezone}${g.utc ? ` (UTC${g.utc})` : ""}` : ""],
    ["Ülke telefon kodu", g.calling_code ? `+${g.calling_code}` : ""],
    ["İnternet sağlayıcı", g.isp],
    ["Kurum / ağ", [g.org, g.asn ? `AS${String(g.asn).replace(/^AS/, "")}` : "", g.domain].filter(Boolean).join(" · ")],
    ["Bağlantı türü", [connOf(e), c.conn_eff ? c.conn_eff.toUpperCase() : "", c.conn_down ? `~${c.conn_down} Mbps` : "", c.conn_rtt ? `${c.conn_rtt} ms gecikme` : "", c.save_data ? "veri tasarrufu açık" : ""].filter(Boolean).join(" · ")],
    ["VPN / Proxy", g.proxy === undefined ? "" : g.proxy ? "EVET — VPN / proxy / Tor olabilir" : "Hayır"],
    ["Cihaz", [d.kind, d.brand, d.model_name || d.model, d.model_name && d.model ? `(${d.model})` : ""].filter((x) => x && x !== "Other").join(" ")],
    ["Tahmini model", d.model_guess],
    ["İşletim sistemi", [d.os, d.os_version].filter(Boolean).join(" ")],
    ["Tarayıcı", [d.browser, d.browser_version].filter(Boolean).join(" ")],
    ["Tarayıcı sürümleri", (c.brands || []).join(" · ")],
    ["Ekran", c.screen ? `${c.screen} · ${c.dpr}x piksel yoğunluğu${c.color_depth ? ` · ${c.color_depth}-bit renk` : ""}${c.orientation ? ` · ${c.orientation.startsWith("portrait") ? "dikey" : "yatay"}` : ""}` : ""],
    ["Görünür alan", c.viewport],
    ["Donanım", [c.cores ? `${c.cores} çekirdek` : "", c.memory ? `${c.memory} GB+ RAM` : "", c.arch ? `${c.arch}${c.bitness ? `-${c.bitness}` : ""}` : ""].filter(Boolean).join(" · ")],
    ["Ekran kartı", c.gpu],
    ["Dokunmatik", c.touch ? `Evet (${c.touch} nokta)` : c.touch === 0 ? "Hayır" : ""],
    ["Platform", c.platform],
    ["Dil", [c.lang, (c.langs || []).join(", ")].filter(Boolean).join(" — ")],
    ["Cihaz saat dilimi", c.tz ? `${c.tz} (UTC${c.tz_offset >= 0 ? "+" : ""}${(c.tz_offset || 0) / 60})` : ""],
    ["Kullanım şekli", c.standalone ? "Ana ekrana eklenmiş uygulama" : c.screen ? "Tarayıcı sekmesi" : ""],
    ["Gelinen sayfa", c.referrer],
    ["Cihaz kimliği", e.device_id ? e.device_id.slice(0, 12) : ""],
    ["Oturum zinciri", e.gid],
    ["Konum kaynağı", g.source],
    ["User-Agent", e.ua],
  ].filter(([, v]) => v !== undefined && v !== null && v !== "");
}

function EventCard({ e, onChain }) {
  const [open, setOpen] = useState(false);
  const k = KIND[e.kind] || KIND.login_ok;
  const Icon = k.icon;
  const DevIcon = KIND_ICON[e.device?.kind] || Monitor;
  const g = e.geo || {};
  const title = e.kind === "login_ok" || e.kind === "unban" ? e.detail : `${k.label}${e.detail ? ` — ${e.detail}` : ""}`;
  return (
    <div className="relative rounded-xl border bg-card overflow-hidden" data-testid={`login-event-${e.id}`}>
      <span className={cn("absolute left-0 inset-y-0 w-1", k.tone)} />
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full text-left p-3 pl-4 flex items-start gap-3 hover:bg-muted/30 transition-colors" data-testid={`login-event-toggle-${e.id}`}>
        <span className={cn("h-9 w-9 rounded-lg grid place-items-center shrink-0 text-white", k.tone)}><Icon className="h-4 w-4" /></span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm">{title}</span>
            <span className={cn("rounded-full px-2 py-px text-[10px] font-bold", k.soft)}>{k.label}</span>
          </div>
          <div className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
            <span className="inline-flex items-center gap-1"><DevIcon className="h-3.5 w-3.5" />{e.device?.summary || "Bilinmeyen cihaz"}</span>
            {(place(g) || g.isp) && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{[g.flag, place(g)].filter(Boolean).join(" ")}{g.isp ? ` · ${g.isp}` : ""}</span>}
            {connOf(e) && <span className="inline-flex items-center gap-1"><Wifi className="h-3.5 w-3.5" />{connOf(e)}</span>}
            <span className="font-mono">{e.ip}</span>
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="font-mono text-sm font-bold tabular-nums" data-testid={`login-event-time-${e.id}`}>{clock(e.at)}</div>
          <ChevronDown className={cn("h-4 w-4 text-muted-foreground ml-auto mt-1 transition-transform", open && "rotate-180")} />
        </div>
      </button>
      {open && (
        <div className="border-t bg-muted/20 px-4 py-3" data-testid={`login-event-details-${e.id}`}>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5 text-xs">
            {rows(e).map(([l, v]) => (
              <div key={l} className={cn("flex gap-3", l === "User-Agent" && "md:col-span-2")}>
                <dt className="w-40 shrink-0 text-muted-foreground">{l}</dt>
                <dd className={cn("min-w-0 break-words font-medium", (l === "User-Agent" || l.startsWith("IP") || l === "Cihaz kimliği") && "font-mono text-[11px]")}>
                  {l === "Oturum zinciri" ? <button type="button" className="text-primary hover:underline font-mono" onClick={() => onChain(v)} data-testid={`login-event-chain-${e.id}`}>{v} · bu oturumun tüm adımları</button> : v}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      )}
    </div>
  );
}

// Aktiviteler › Girişler: siteye bağlanan herkesin ayrıntılı kaydı + aktif engeller
export default function LoginEvents() {
  const [items, setItems] = useState([]);
  const [kind, setKind] = useState("");
  const [q, setQ] = useState("");
  const { ov, load: loadOv, unban, busy } = useSecurityOverview();
  const load = () => api.get("/security/events", { params: { limit: 400, kind, q } }).then((r) => setItems(r.data)).catch(() => {});
  useAutoRefresh(() => { load(); loadOv(); });
  useEffect(() => { load(); }, [kind, q]); // eslint-disable-line react-hooks/exhaustive-deps
  const groups = useMemo(() => {
    const m = new Map();
    items.forEach((e) => { const d = dayOf(e.at); if (!m.has(d)) m.set(d, []); m.get(d).push(e); });
    return [...m.entries()];
  }, [items]);
  const active = (ov?.bans || []).filter((b) => b.active);
  return (
    <div className="space-y-4" data-testid="login-events">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Başarılı Giriş (24 sa)" value={ov?.stats?.ok_24h ?? "—"} icon={LogIn} tone="success" testid="login-kpi-ok" onClick={() => setKind("login_ok")} active={kind === "login_ok"} />
        <StatCard label="Hatalı Deneme (24 sa)" value={ov?.stats?.fail_24h ?? "—"} icon={ShieldAlert} tone="warning" testid="login-kpi-fail" onClick={() => setKind("fail")} active={kind === "fail"} />
        <StatCard label="Aktif Engel" value={ov?.stats?.active_bans ?? "—"} icon={Ban} tone="danger" testid="login-kpi-bans" onClick={() => setKind("ban")} active={kind === "ban"} />
        <StatCard label="Bu Cihazın IP'si" value={<span className="text-base font-mono">{ov?.my_ip || "—"}</span>} icon={Monitor} testid="login-kpi-myip" />
      </div>
      {active.length > 0 && (
        <div className="rounded-xl border border-red-300 bg-red-50/50 dark:bg-red-500/5 p-3 space-y-2" data-testid="login-active-bans">
          <div className="text-xs font-bold uppercase tracking-wider text-red-700">Aktif engeller</div>
          <BanList bans={active} onUnban={async (b) => { await unban(b); load(); }} busy={busy} showHistory={false} />
        </div>
      )}
      <p className="text-[11px] text-muted-foreground flex items-start gap-1.5 rounded-lg bg-muted/40 px-3 py-2" data-testid="login-privacy-note">
        <Info className="h-3.5 w-3.5 mt-px shrink-0" />Konum IP adresinden tahmin edilir (şehir düzeyi). Telefon numarası, IMEI ve rehber gibi bilgileri tarayıcılar hiçbir web sitesine vermez; bu yüzden kaydedilemez. iPhone modeli ekran ölçüsünden tahmin edilir; Android'de model kodu tarayıcıdan alınır.
      </p>
      <div className="flex flex-col lg:flex-row gap-3 lg:items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Kullanıcı, IP, şehir, operatör, cihaz…" className="pl-9" data-testid="login-search" />
        </div>
        <Segmented value={kind} onChange={setKind} testid="login-filter" size="xs"
          options={[["", "Tümü"], ["login_ok", "Girişler"], ["gate_ok", "Parola"], ["fail", "Hatalı"], ["ban", "Engeller"]]} />
      </div>
      {!items.length ? <Empty title="Kayıt yok" desc="Seçili filtrede giriş kaydı bulunamadı" /> : groups.map(([day, list]) => (
        <div key={day} className="space-y-2">
          <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">{day} · {list.length} kayıt</div>
          {list.map((e) => <EventCard key={e.id} e={e} onChain={(gid) => setQ(gid)} />)}
        </div>
      ))}
    </div>
  );
}
